/**
 * The Star Battle screen.
 *
 * One component tree, no router: the game is a single 10×10 board plus its
 * controls, and everything it needs — the puzzle, the marks, the clock — lives in
 * this component and is mirrored to localStorage on every change.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';

import {
  analyseBoard,
  applyMarks,
  cycleMark,
  dragMark,
  emptyBoard,
  impliedCrosses,
  nextHint,
  type Board as PlayerBoard,
  type BoardStatus,
  type Change,
  type Hint,
  type Unit,
} from './game';
import { Generator } from './generatorClient';
import {
  CELLS,
  DIFFICULTIES,
  DIFFICULTY_LABEL,
  DOT,
  EMPTY,
  GLYPH_NOUN,
  SIZE,
  STAR,
  STARS,
  type Difficulty,
  type Glyph,
  type Mark,
  type Puzzle,
} from './types';
import { load, save, type Settings, type Stored } from './storage';

const UNDO_LIMIT = 200;
const TOTAL_STARS = SIZE * STARS;

// ── Cell glyphs ─────────────────────────────────────────────────────────────

function Cross(): JSX.Element {
  return (
    <svg class="sb-glyph sb-cross" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 7 L17 17 M17 7 L7 17" />
    </svg>
  );
}

function Star(): JSX.Element {
  return (
    <svg class="sb-glyph sb-star" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.6 14.9 9.1 22 9.9 16.7 14.7 18.2 21.7 12 18.1 5.8 21.7 7.3 14.7 2 9.9 9.1 9.1 Z" />
    </svg>
  );
}

/**
 * The other thing a star can be.
 *
 * An emoji rather than a drawing of a cat: it comes with the platform, it is
 * the same cat the player already knows, and it costs nothing to ship.
 *
 * Set in the same 24-unit box as the drawn glyphs rather than as text in the
 * cell, which is what centres it. Laid out as text it is a line box on a
 * baseline — the ink sits wherever the emoji font's metrics put it, which was
 * visibly left of and above the middle of the square. Here the anchoring is
 * SVG's, from the centre of the box outwards, and it scales with the board for
 * the same reason the star does.
 */
function Cat(): JSX.Element {
  return (
    <svg class="sb-glyph sb-cat" viewBox="0 0 24 24" aria-hidden="true">
      <text x="12" y="12">
        🐱
      </text>
    </svg>
  );
}

function Placed({ glyph }: { glyph: Glyph }): JSX.Element {
  return glyph === 'cat' ? <Cat /> : <Star />;
}

// ── Tool icons ──────────────────────────────────────────────────────────────

/**
 * The three board tools, drawn rather than labelled.
 *
 * One stroke weight, one 24-unit box, no fills: they sit inside identical
 * squircles at a size where any interior detail would turn to mud.
 */
function Icon({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element {
  return (
    <svg class="sb-icon" viewBox="0 0 24 24" aria-hidden="true">
      {children}
    </svg>
  );
}

function HintIcon(): JSX.Element {
  return (
    <Icon>
      <path d="M12 3.2a5.2 5.2 0 0 0-3 9.4c.6.5 1 1.1 1 1.9v.3h4v-.3c0-.8.4-1.4 1-1.9a5.2 5.2 0 0 0-3-9.4Z" />
      <path d="M9.8 17.4h4.4" />
      <path d="M10.6 20.3h2.8" />
    </Icon>
  );
}

function UndoIcon(): JSX.Element {
  return (
    <Icon>
      <path d="M4.5 9.2h9a5.3 5.3 0 0 1 0 10.6H8.2" />
      <path d="M8 4.7 3.6 9.2 8 13.7" />
    </Icon>
  );
}

function CrossesIcon(): JSX.Element {
  return (
    <Icon>
      <path d="M4.6 4.6 12 12M12 4.6 4.6 12" />
      <path d="M16.2 6.4 20.4 10.6M20.4 6.4 16.2 10.6" />
      <path d="M15.8 15.8 20.6 20.6M20.6 15.8 15.8 20.6" />
    </Icon>
  );
}

function TrashIcon(): JSX.Element {
  return (
    <Icon>
      <path d="M4.5 6.8h15" />
      <path d="M9.5 6.8V4.6h5v2.2" />
      <path d="m6.8 6.8 1 12.6h8.4l1-12.6" />
      <path d="M10.4 10.4v5.6M13.6 10.4v5.6" />
    </Icon>
  );
}

interface ToolProps {
  label: string;
  /** What the button does, for anyone who cannot see the icon. */
  description: string;
  disabled: boolean;
  onClick: () => void;
  children: JSX.Element;
}

function Tool({ label, description, disabled, onClick, children }: ToolProps): JSX.Element {
  return (
    <button
      type="button"
      class="sb-tool"
      onClick={onClick}
      disabled={disabled}
      title={description}
      aria-label={description}
    >
      <span class="sb-tool-face" aria-hidden="true">
        {children}
      </span>
      {/* Visible, not a tooltip: an icon nobody can name is a button nobody
          presses, and there is room for a word under each. */}
      <span class="sb-tool-name">{label}</span>
    </button>
  );
}

// ── Board ───────────────────────────────────────────────────────────────────

/**
 * The lines between regions, as SVG in board coordinates.
 *
 * Drawn over the grid rather than as cell borders: a border belongs to one cell
 * and a region edge belongs to two, so borders end up either doubled or half a
 * pixel off. In a `0 0 SIZE SIZE` viewBox the segments are integers.
 */
function regionEdges(regions: readonly number[]): string {
  const parts: string[] = [];
  for (let cell = 0; cell < CELLS; cell += 1) {
    const row = Math.floor(cell / SIZE);
    const column = cell % SIZE;
    if (row > 0 && regions[cell - SIZE] !== regions[cell]) parts.push(`M${column} ${row}h1`);
    if (column > 0 && regions[cell - 1] !== regions[cell]) parts.push(`M${column} ${row}v1`);
  }
  return parts.join('');
}

interface BoardProps {
  puzzle: Puzzle;
  marks: Mark[];
  glyph: Glyph;
  errors: ReadonlySet<number>;
  highlight: ReadonlySet<number>;
  frozen: boolean;
  /** Finished: the frame keeps a ring of starlight. See `.sb-solved` in the CSS. */
  solved: boolean;
  /** Finished *here*, a moment ago, rather than restored already finished. */
  celebrating: boolean;
  onApply: (changes: Change[]) => void;
}

function Board(props: BoardProps): JSX.Element {
  const { puzzle, marks, glyph, errors, highlight, frozen, solved, celebrating, onApply } = props;
  const edges = useMemo(() => regionEdges(puzzle.regions), [puzzle.regions]);

  // A tap cycles the cell; a drag lays down crosses or rubs them out. Which one
  // it was is only known at the end, so the origin cell is held until the
  // pointer either moves to another cell or lifts where it started. The origin's
  // mark also decides which way the drag runs — see `dragMark`.
  const origin = useRef<number | null>(null);
  // The origin's mark as it was when the finger went down. Held rather than read
  // back from `marks`, because rubbing out the square the drag started on is the
  // first thing an erasing drag does — and a mode that reads itself back would
  // find a blank square there and spend the rest of the stroke crossing out.
  const originMark = useRef<Mark>(EMPTY);
  const dragged = useRef(false);
  const painted = useRef(new Set<number>());

  const paint = useCallback(
    (cell: number): void => {
      if (painted.current.has(cell)) return;
      painted.current.add(cell);
      const mark = dragMark(originMark.current, marks[cell] as Mark);
      if (mark === null) return;
      onApply([{ cell, mark }]);
    },
    [marks, onApply],
  );

  const cellAt = (event: PointerEvent): number | null => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    const raw = element?.closest('[data-cell]')?.getAttribute('data-cell');
    return raw === null || raw === undefined ? null : Number(raw);
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (frozen || event.button !== 0) return;
    const cell = cellAt(event);
    if (cell === null) return;
    origin.current = cell;
    originMark.current = marks[cell] as Mark;
    dragged.current = false;
    painted.current.clear();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (frozen || origin.current === null) return;
    const cell = cellAt(event);
    if (cell === null) return;
    if (!dragged.current) {
      if (cell === origin.current) return;
      // Only now is it a drag, so the square it started on is marked too.
      dragged.current = true;
      paint(origin.current);
    }
    paint(cell);
  };

  const endPointer = (): void => {
    if (origin.current !== null && !dragged.current) {
      const cell = origin.current;
      onApply([{ cell, mark: cycleMark(marks[cell] as Mark) }]);
    }
    origin.current = null;
    dragged.current = false;
    painted.current.clear();
  };

  const onKeyDown = (event: KeyboardEvent, cell: number): void => {
    if (frozen) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onApply([{ cell, mark: cycleMark(marks[cell] as Mark) }]);
      return;
    }
    const moves: Record<string, number> = {
      ArrowUp: -SIZE,
      ArrowDown: SIZE,
      ArrowLeft: -1,
      ArrowRight: 1,
    };
    const step = moves[event.key];
    if (step === undefined) return;
    const target = cell + step;
    if (target < 0 || target >= CELLS) return;
    // Left and right must not wrap onto the next row.
    if (Math.abs(step) === 1 && Math.floor(target / SIZE) !== Math.floor(cell / SIZE)) return;
    event.preventDefault();
    document.querySelector<HTMLElement>(`[data-cell="${target}"]`)?.focus();
  };

  return (
    <div
      class={`sb-board${solved ? ' sb-solved' : ''}${celebrating ? ' sb-just-solved' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onPointerLeave={endPointer}
      role="grid"
      aria-label={`${SIZE} by ${SIZE} star battle grid`}
    >
      <div class="sb-cells">
        {marks.map((mark, cell) => (
          <div
            key={cell}
            data-cell={cell}
            class={`sb-cell${errors.has(cell) ? ' sb-cell-error' : ''}${
              highlight.has(cell) ? ' sb-cell-hint' : ''
            }`}
            // The diagonal this cell sits on, which is all the markup
            // contributes to the finish: it staggers the celebration into a
            // wave rolling off the top-left corner instead of a flashbulb.
            style={`--sb-wave:${Math.floor(cell / SIZE) + (cell % SIZE)}`}
            role="gridcell"
            tabIndex={0}
            aria-label={`Row ${Math.floor(cell / SIZE) + 1}, column ${(cell % SIZE) + 1}: ${
              mark === STAR ? GLYPH_NOUN[glyph].one : mark === DOT ? 'crossed out' : 'empty'
            }`}
            onKeyDown={(event: KeyboardEvent) => onKeyDown(event, cell)}
          >
            {mark === DOT && <Cross />}
            {mark === STAR && <Placed glyph={glyph} />}
          </div>
        ))}
      </div>
      <svg
        class="sb-edges"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path d={edges} />
      </svg>
    </div>
  );
}

// ── Confirmation ────────────────────────────────────────────────────────────

/**
 * The one action on this screen that cannot be taken back in one press.
 *
 * Undo restores the marks a clear threw away — the history keeps them — but a
 * player who has just watched forty minutes of crosses vanish does not know
 * that, and should not have to. A real `<dialog>` opened with `showModal`
 * rather than an overlay of our own: the backdrop, the focus trap and Escape
 * all come with it.
 */
function ConfirmClear({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  return (
    <dialog
      class="sb-dialog"
      ref={dialog}
      onCancel={(event: Event) => {
        // Escape: let it close through the same path as the button, so the
        // component unmounts instead of leaving a closed dialog mounted.
        event.preventDefault();
        onCancel();
      }}
      onClick={(event: MouseEvent) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div class="sb-dialog-body">
        <h2>Clear the board?</h2>
        <p>Every star and cross you have placed goes. The puzzle and the clock stay.</p>
        <div class="sb-dialog-buttons">
          <button type="button" onClick={onCancel}>
            Keep playing
          </button>
          <button type="button" class="sb-destructive" onClick={onConfirm}>
            Clear board
          </button>
        </div>
      </div>
    </dialog>
  );
}

// ── Feedback timing ─────────────────────────────────────────────────────────

interface Faults {
  errors: Set<number>;
  starved: Unit[];
}

const NO_FAULTS: Faults = { errors: new Set(), starved: [] };

/**
 * How long the board must hold a fault before it is mentioned.
 *
 * Long enough to cover the gap between the two taps that make a star. A mark
 * cycles empty → ✕ → star, so putting the last star into a row means passing
 * through a ✕ that leaves that row no room — and the complaint about it landed
 * on the first tap and was gone on the second, which read as a flicker rather
 * than as feedback about anything.
 */
const SETTLE_MS = 600;

/**
 * The board's faults, once they have stopped moving.
 *
 * Only the *appearance* of a fault waits. Once something is being complained
 * about, changes and withdrawals are immediate: nothing flickers on the way to
 * saying less, and a stale error would leave a red square under a star the
 * player has already picked up.
 */
function useSettledFaults(status: BoardStatus | null): Faults {
  const [faults, setFaults] = useState<Faults>(NO_FAULTS);
  const showing = useRef(false);

  useEffect(() => {
    const found = !!status && (status.errors.size > 0 || status.starved.length > 0);
    if (!found || !status) {
      showing.current = false;
      setFaults(NO_FAULTS);
      return undefined;
    }
    const next: Faults = { errors: status.errors, starved: status.starved };
    if (showing.current) {
      setFaults(next);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      showing.current = true;
      setFaults(next);
    }, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  return faults;
}

// ── Screen ──────────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

export function App(): JSX.Element {
  const generator = useMemo(() => new Generator(), []);
  const initial = useMemo<Stored>(() => load(), []);

  const [difficulty, setDifficulty] = useState(initial.difficulty);
  const [settings, setSettings] = useState<Settings>(initial.settings);
  const [puzzle, setPuzzle] = useState<Puzzle | null>(initial.game?.puzzle ?? null);
  const [board, setBoard] = useState<PlayerBoard>(() =>
    initial.game ? { marks: initial.game.marks, sources: initial.game.sources } : emptyBoard(),
  );
  const [elapsed, setElapsed] = useState(initial.game?.elapsed ?? 0);
  const [completed, setCompleted] = useState(initial.game?.completed ?? false);
  // Whether the board was finished in front of the player rather than restored
  // already finished. Deliberately not saved: coming back to a solved board is
  // not the same event as solving one, and the celebration belongs to the
  // second. A finished board still looks finished either way.
  const [celebrating, setCelebrating] = useState(false);
  const [best, setBest] = useState(initial.best);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [hint, setHint] = useState<Hint | null>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);
  // Whole boards, not just marks: undoing a star has to put back the crosses it
  // took away *and* who owned them, and a snapshot is the only version of that
  // which cannot drift from what the player saw.
  const history = useRef<PlayerBoard[]>([]);

  const marks = board.marks;
  const noun = GLYPH_NOUN[settings.glyph];
  const status = useMemo(() => (puzzle ? analyseBoard(puzzle, marks) : null), [puzzle, marks]);
  const faults = useSettledFaults(status);

  // ── Persistence and the clock ────────────────────────────────────────────

  useEffect(() => {
    save({
      difficulty,
      settings,
      game: puzzle
        ? { puzzle, marks: board.marks, sources: board.sources, elapsed, completed }
        : null,
      best,
    });
  }, [difficulty, settings, puzzle, board, elapsed, completed, best]);

  useEffect(() => {
    if (!puzzle || completed) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') setElapsed((value) => value + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [puzzle, completed]);

  // Finishing is detected from the board, not from a "check" button: the moment
  // the last star lands the puzzle is over, and the clock should stop there.
  useEffect(() => {
    if (!puzzle || !status?.solved || completed) return;
    setCompleted(true);
    // A board restored from storage arrives with `completed` already true and
    // never reaches here, which is what keeps the celebration to the move that
    // earned it.
    setCelebrating(true);
    setBest((previous) => {
      const current = previous[puzzle.difficulty];
      if (current !== undefined && current <= elapsed) return previous;
      return { ...previous, [puzzle.difficulty]: elapsed };
    });
  }, [puzzle, status?.solved, completed, elapsed]);

  // ── Generation ───────────────────────────────────────────────────────────

  const startPuzzle = useCallback(
    async (want: Difficulty): Promise<void> => {
      setBusy(true);
      setProblem(null);
      try {
        const next = await generator.request(want);
        setPuzzle(next);
        setBoard(emptyBoard());
        setElapsed(0);
        setCompleted(false);
        setCelebrating(false);
        setHint(null);
        setConfirmingClear(false);
        history.current = [];
        generator.warm(want);
      } catch (error) {
        setProblem(
          error instanceof Error
            ? `Could not build a puzzle: ${error.message}`
            : 'Could not build a puzzle.',
        );
      } finally {
        setBusy(false);
      }
    },
    [generator],
  );

  // First visit with nothing saved: build something to play rather than showing
  // an empty frame with a button on it.
  useEffect(() => {
    if (!puzzle && !busy) void startPuzzle(difficulty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (puzzle && !busy) generator.warm(difficulty);
  }, [generator, puzzle, busy, difficulty]);

  // ── Board edits ──────────────────────────────────────────────────────────

  const apply = useCallback(
    (changes: Change[]): void => {
      if (changes.length === 0 || completed) return;
      // Any edit can invalidate the hint, and a highlight left over from before
      // the move points somewhere that may no longer be the place to look.
      setHint(null);
      setBoard((previous) => {
        const next = applyMarks(previous, changes, settings.autoCross);
        if (next === previous) return previous;
        history.current.push(previous);
        if (history.current.length > UNDO_LIMIT) history.current.shift();
        return next;
      });
    },
    [completed, settings.autoCross],
  );

  const undo = useCallback((): void => {
    const previous = history.current.pop();
    if (previous) {
      setBoard(previous);
      setCompleted(false);
      setCelebrating(false);
      setHint(null);
    }
  }, []);

  const clear = useCallback((): void => {
    history.current.push(board);
    setBoard(emptyBoard());
    setCompleted(false);
    setCelebrating(false);
    setHint(null);
    setConfirmingClear(false);
  }, [board]);

  const showHint = useCallback((): void => {
    if (puzzle) setHint(nextHint(puzzle, marks));
  }, [puzzle, marks]);

  const tidy = useCallback((): void => {
    if (!puzzle) return;
    apply(impliedCrosses(puzzle, marks).map((cell) => ({ cell, mark: DOT as Mark })));
  }, [puzzle, marks, apply]);

  const changeDifficulty = (next: Difficulty): void => {
    setDifficulty(next);
    void startPuzzle(next);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const bestTime = puzzle ? best[puzzle.difficulty] : undefined;
  const starved = faults.starved;
  const hintCells = useMemo(() => new Set(hint?.cells ?? []), [hint]);
  const marked = useMemo(() => marks.some((mark) => mark !== EMPTY), [marks]);

  return (
    <main class="sb-app">
      <header class="sb-header">
        <a class="sb-back" href="/">
          ← Games
        </a>
        <h1>Star Battle</h1>
        <p class="sb-rule">
          Two {noun.many} in every row, column and outlined region. They may never touch, not even
          diagonally.
        </p>
      </header>

      <section class="sb-controls" aria-label="Puzzle settings">
        <label class="sb-field">
          <span>Difficulty</span>
          <select
            value={difficulty}
            disabled={busy}
            onChange={(event) =>
              changeDifficulty((event.target as HTMLSelectElement).value as Difficulty)
            }
          >
            {DIFFICULTIES.map((option) => (
              <option key={option} value={option}>
                {DIFFICULTY_LABEL[option]}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          class="sb-primary"
          disabled={busy}
          onClick={() => void startPuzzle(difficulty)}
        >
          {busy ? 'Building…' : 'New puzzle'}
        </button>

        {/* Their own line under the difficulty row: these two outlive the
            puzzle above them, and neither is a thing you press to get a board. */}
        <div class="sb-options">
          <label class="sb-check">
            <input
              type="checkbox"
              checked={settings.autoCross}
              onChange={(event) =>
                setSettings((previous) => ({
                  ...previous,
                  autoCross: (event.target as HTMLInputElement).checked,
                }))
              }
            />
            <span>Cross out around each {noun.one}</span>
          </label>
          <label class="sb-check">
            <input
              type="checkbox"
              checked={settings.glyph === 'cat'}
              onChange={(event) =>
                setSettings((previous) => ({
                  ...previous,
                  glyph: (event.target as HTMLInputElement).checked ? 'cat' : 'star',
                }))
              }
            />
            <span>Cats instead of stars 🐱</span>
          </label>
        </div>
      </section>

      {problem && (
        <p class="sb-problem" role="alert">
          {problem}
        </p>
      )}

      {busy && (
        <p class="sb-building" role="status">
          <span class="sb-spinner" aria-hidden="true" />
          Building a puzzle…
        </p>
      )}

      {puzzle && (
        <>
          <div class="sb-status">
            <span class="sb-clock">{formatTime(elapsed)}</span>
            <span class="sb-count">
              {status?.placed ?? 0}/{TOTAL_STARS} {noun.many}
            </span>
            {bestTime !== undefined && <span class="sb-best">best {formatTime(bestTime)}</span>}
          </div>

          <div class={`sb-frame${busy ? ' sb-frame-busy' : ''}`}>
            <Board
              puzzle={puzzle}
              marks={marks}
              glyph={settings.glyph}
              errors={faults.errors}
              highlight={hintCells}
              frozen={completed || busy}
              solved={completed}
              celebrating={celebrating}
              onApply={apply}
            />
          </div>

          {/* Below the board rather than over it: the finished grid is the thing
              worth looking at when you finish, and a panel across it hides the
              last row of stars you just placed. */}
          {completed && (
            <div class={`sb-win${celebrating ? ' sb-win-new' : ''}`} role="status">
              <strong>Solved!</strong>
              <span>
                {DIFFICULTY_LABEL[puzzle.difficulty]} · {formatTime(elapsed)}
              </span>
              <button type="button" class="sb-primary" onClick={() => void startPuzzle(difficulty)}>
                Play another
              </button>
            </div>
          )}

          {faults.errors.size > 0 && (
            <p class="sb-warning" role="status">
              Highlighted {noun.many} break a rule — they touch, or their row, column or region
              already has {STARS}.
            </p>
          )}
          {starved.length > 0 && (
            <p class="sb-warning sb-warning-soft" role="status">
              No room left for enough stars in{' '}
              {starved
                .slice(0, 3)
                .map((unit) => `${unit.kind} ${unit.index + 1}`)
                .join(', ')}
              {starved.length > 3 ? ` and ${starved.length - 3} more` : ''}.
            </p>
          )}

          {hint && (
            <p class="sb-hint-box" role="status">
              💡 {hint.message}
            </p>
          )}

          <div class="sb-actions">
            <Tool label="Hint" description="Show a hint" disabled={completed} onClick={showHint}>
              <HintIcon />
            </Tool>
            <Tool label="Undo" description="Undo the last move" disabled={completed} onClick={undo}>
              <UndoIcon />
            </Tool>
            <Tool
              label="Fill"
              description="Fill in the crosses your stars force"
              disabled={completed}
              onClick={tidy}
            >
              <CrossesIcon />
            </Tool>
          </div>

          {/* Its own row, well clear of the three above, and behind a question.
              As the fourth button in that grid it was a thumb's width from undo,
              and the two are not equally survivable mistakes. */}
          <div class="sb-clear-row">
            <button
              type="button"
              class="sb-clear"
              disabled={completed || !marked}
              onClick={() => setConfirmingClear(true)}
            >
              <TrashIcon />
              Clear board
            </button>
          </div>

          {confirmingClear && (
            <ConfirmClear onCancel={() => setConfirmingClear(false)} onConfirm={clear} />
          )}

          <p class="sb-hint">
            Tap once to cross a square out, again to place a {noun.one}, again to clear it. Drag
            from a blank square to cross out a run of them, or from a crossed one to rub the run out
            again.
            {settings.autoCross && (
              <>
                {' '}
                The squares around a {noun.one} are crossed out as you place it, and uncrossed if
                you take it back — unless you had crossed them yourself.
              </>
            )}
          </p>
          <p class="sb-meta">
            {DIFFICULTY_LABEL[puzzle.difficulty]} · seed {puzzle.seed.toString(36)}
            {puzzle.difficulty !== difficulty && (
              <> · closest to {DIFFICULTY_LABEL[difficulty]} we could build</>
            )}
          </p>
        </>
      )}
    </main>
  );
}
